import React, { useState } from 'react';

import { Row, Col, notification } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { datePickerToApi, getNow } from '@libs/NwMoment';
import { getMoment } from '@libs/NwMoment';
import { validateAmountZeroAllowed } from '@libs/NwUtils';
import { getProjectDescriptionsOptions, getProjectTitlesOptions } from '@libs/ProjectUtils';

import { useProjectContext } from '@contexts/ProjectContext';
import { useStateValue } from '@contexts/StateProvider';

import { forceReloadProject, onGetOrderedEventList } from '@hooks/API/Project/UseProject';
import { forceReloadProjectAccountingDetails } from '@hooks/API/Project/UseProjectAccountingDetails';
import { forceReloadProjectStatements } from '@hooks/API/Project/UseProjectStatements';
import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';
import useTransactionLabels from '@hooks/CachedLists/UseTransactionLabels';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { Loading } from '@components/Gui/Loading';
import { NwFakeFormItem } from '@components/Gui/NwForm/NwFakeFormWrapper';
import {
    NWAutoComplete,
    NWTextAreaAutoComplete,
    NWContactWithBillingInfo,
    NWAmountInput,
    NWBookingEventSelectorForTransactions,
    NWDatePicker,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import { ProjectTransactionFormContainer } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';
import {
    getSingleConfirmedJobEvent,
    handleChangeEventForDate,
    getProjectModelsForTransactions,
} from '@components/Project/ProjectTransactions/Transactions/TransactionCreateFormUtils';

import NwFixedCancelAndSaveBar from '@components/Gui/NwFixedCancelAndSaveBar';
import ModelSelectorProject from './Common/ModelSelectorProject';
import TransactionLabelsSelector from './Common/TransactionLabelsSelector';

const AgencyFeeForm = ({ onCloseDrawer, clientBillingInfos, contacts, width, minWidth }) => {
    //onChangeForm
    const [{ areaContext }] = useStateValue();

    const contextData = useProjectContext();
    const { projectData: currentProject, transactionsGrouped } = contextData;

    const transactionTitles = [
        ...new Set(
            transactionsGrouped.flatMap((group) => group.Dates.flatMap((date) => date.Transactions.map((t) => t.Title)))
        ),
    ].filter(Boolean);

    const transactionDescriptions = [
        ...new Set(
            transactionsGrouped.flatMap((group) =>
                group.Dates.flatMap((date) => date.Transactions.map((t) => t.Description))
            )
        ),
    ].filter(Boolean);

    const transactionProperties = useTransactionLabels('AgFee');
    const getTransactionLabel = (transactionLabelID) =>
        transactionProperties.labels.find((label) => label.ID === transactionLabelID);

    const configurations = useConfigurations();
    const events = onGetOrderedEventList(currentProject, areaContext, false, false);
    // Se il progetto ha un singolo evento job/ext e quello e' confermato,
    // pre-popoliamo il selettore evento e i campi correlati.
    const preselectedEvent = getSingleConfirmedJobEvent(events);

    const [groupedModelDates, setGroupedModelDates] = useState(getProjectModelsForTransactions(currentProject));

    const saveTransaction = async (values, setSubmitting) => {
        //TODO: verificare validazione
        let apiData = {
            ProjectID: currentProject.ID,
            ClientID: values.ContactToBill.ContactID,
            ClientBillingInfoID: values.ContactToBill.ContactBillingInfoID,
            Family: 'AgencyFee',
            TransactionLabelID: values.TransactionLabelID,
            Title: values.Title,
            Description: values.Description,
            Quantity: 1,
            TypeQuantity: 'Units',
            Currency: values.Amount[0],
            SingleFee: values.Amount[1],
            RefDate: values.RefDate && datePickerToApi(values.RefDate),
        };
        if (values.SelectedEvent) {
            apiData.EventID = values.SelectedEvent.ID;
        }

        let modelID = null;
        for (const groupedModel of groupedModelDates) {
            if (groupedModel.Active) {
                modelID = groupedModel.ModelID;
            }
        }

        if (!modelID) {
            setSubmitting(false);
            notification.error({
                message: 'Error',
                description: 'Please select at least one model',
                placement: 'bottomRight',
                bottom: 100,
                duration: 0,
            });
        } else {
            apiData = {
                ...apiData,
                ModelID: modelID,
            };

            try {
                await Axios.post('/transactions/agfee', apiData);
                forceReloadProject(currentProject.ID);
                forceReloadProjectAccountingDetails(currentProject.ID);
                forceReloadProjectTransactions(currentProject.ID);
                forceReloadProjectStatements(currentProject.ID);
                onCloseDrawer();
                setSubmitting(false);
            } catch (error) {
                console.error('Create Agency Fee Error => ', error);
                setSubmitting(false);
            }
        }
    };

    const clientBillingInfoDefault = clientBillingInfos.find((item) => item.isDefault);
    let clientBillingInfoDefaultID;
    if (clientBillingInfoDefault) {
        clientBillingInfoDefaultID = clientBillingInfoDefault.ID;
    } else {
        if (clientBillingInfoDefault && clientBillingInfoDefault.length > 0) {
            clientBillingInfoDefaultID = clientBillingInfoDefault[0].ID;
        }
    }

    const initialValues = {
        ContactToBill: { ContactID: currentProject.CustomerID, ContactBillingInfoID: clientBillingInfoDefaultID },
        TransactionLabelID: transactionProperties.startLabel.ID,
        Title: preselectedEvent
            ? `${transactionProperties.startLabel.Label} for ${preselectedEvent.Name}`
            : transactionProperties.startLabel.Label,
        Description: '',
        SelectedEvent: preselectedEvent,
        Amount: [configurations.defaultCurrency, null],
        RefDate: preselectedEvent
            ? getMoment(preselectedEvent.FirstDate)
            : currentProject.FirstDate
              ? getMoment(currentProject.FirstDate)
              : getNow(),
    };

    return currentProject ? (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                Title: Yup.string().required('Title is required'),
                TransactionLabelID: Yup.string().required('Label is required'),
                ContactToBill: Yup.string().required('Client is required'),
            })}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                saveTransaction(values, setSubmitting);
            }}
        >
            {({ isSubmitting, values, setFieldValue, handleSubmit, touched, isValid }) => (
                <NwForm
                    values={values}
                    //onTouchForm={onChangeForm}
                    onFinish={handleSubmit}
                    layout="vertical"
                >
                    <ProjectTransactionFormContainer>
                        <Row gutter={32}>
                            <Col span={24}>
                                <Field
                                    component={NWBookingEventSelectorForTransactions}
                                    label="Event"
                                    name="SelectedEvent"
                                    events={events}
                                    eventSelectedUniqID={preselectedEvent ? preselectedEvent.uniqID : undefined}
                                    onAfterChange={(value) => {
                                        if (value) {
                                            const labelSelected = values.TransactionLabelID;
                                            const transactionLabel = getTransactionLabel(labelSelected);
                                            setFieldValue('Title', `${transactionLabel.Label} for ${value.Name}`);
                                            handleChangeEventForDate(value, setFieldValue);
                                        }
                                    }}
                                />
                                <NwFakeFormItem
                                    label="Model"
                                    control={
                                        <ModelSelectorProject
                                            models={groupedModelDates}
                                            updateModels={setGroupedModelDates}
                                            uniqueSelection
                                        />
                                    }
                                />

                                <TransactionLabelsSelector
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    transactionProperties={transactionProperties}
                                    updateTitle
                                />
                                <Field
                                    component={NWContactWithBillingInfo}
                                    fullSelector
                                    value={values.ContactToBill}
                                    label="Client"
                                    name="ContactToBill"
                                    contacts={contacts}
                                    billingInfos={clientBillingInfos}
                                    disabled={contacts.length === 1}
                                    required
                                />
                                <Field
                                    component={NWDatePicker}
                                    label="Date"
                                    name="RefDate"
                                    format="MMM DD, YYYY"
                                    allowClear={false}
                                    value={values.RefDate}
                                />
                                <Field
                                    component={NWAutoComplete}
                                    label="Title"
                                    name="Title"
                                    value={values.Title}
                                    maxLength={255}
                                    options={[
                                        ...(transactionTitles.length > 0
                                            ? [
                                                  {
                                                      label: 'Other Transactions',
                                                      options: transactionTitles.map((title) => ({
                                                          label: title,
                                                          value: title,
                                                      })),
                                                  },
                                              ]
                                            : []),
                                        ...getProjectTitlesOptions(currentProject),
                                    ]}
                                />
                                <Field
                                    component={NWTextAreaAutoComplete}
                                    label="Description"
                                    name="Description"
                                    type="text"
                                    value={values.Description}
                                    maxLength={2500}
                                    autoSize={{ minRows: 4, maxRows: 10 }}
                                    options={[
                                        ...(transactionDescriptions.length > 0
                                            ? [
                                                  {
                                                      label: 'Other Transactions',
                                                      options: transactionDescriptions.map((description) => ({
                                                          label: description,
                                                          value: description,
                                                      })),
                                                  },
                                              ]
                                            : []),
                                        ...getProjectDescriptionsOptions(currentProject),
                                    ]}
                                />
                                <Field
                                    component={NWAmountInput}
                                    value={values.Amount}
                                    label="Amount"
                                    name="Amount"
                                    validate={validateAmountZeroAllowed}
                                />
                            </Col>
                        </Row>
                    </ProjectTransactionFormContainer>
                    <NwFixedCancelAndSaveBar
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        onCancel={onCloseDrawer}
                        width={width}
                        minWidth={minWidth}
                    />
                </NwForm>
            )}
        </Formik>
    ) : (
        <Loading />
    );
};

export default AgencyFeeForm;
